// Vendor imports
const classNames = require('classnames')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const { CapsuleButton, CircleButton } = require('client-v2/common/components/buttons')
const { ModalDepthModifier } = require('client-v2/common/contexts/modal-depth')
const CollectionModelRecord = require('client-v2/common/models/collection/record')
const tag = require('client-v2/common/utils/hyperscript')
const {
  keyCodesByName,
  preventDefault,
  stopPropagation
} = require('client-v2/common/utils/events')
// Sibling imports
const AddToCollectionItem = require('./item')

const MAX_COLLECTION_NAME_LENGTH = 50

const bem = BemEntity('AddToCollectionModal')

class AddToCollectionModal extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      hover: false
    }

    this.modalRef = React.createRef()

    // Bind callbacks
    this.onCollectionClick = this.onCollectionClick.bind(this)
    this.onDismissCapture = this.onDismissCapture.bind(this)
    this.onFilterReset = this.onFilterReset.bind(this)
    this.onFilterSubmit = this.onFilterSubmit.bind(this)
    this.onFilterTextChange = this.onFilterTextChange.bind(this)
    this.onFilterTextKeyDown = this.onFilterTextKeyDown.bind(this)
    this.onMouseEnter = () => this.setState({ hover: true })
    this.onMouseLeave = () => this.setState({ hover: false })
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      document.addEventListener('click', this.onDismissCapture, true)
    }
  }

  componentWillUnmount() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('click', this.onDismissCapture, true)
    }
  }

  componentDidUpdate() {
    // Re-position the modal when its content changes
    if (this.props.onLayoutChange) {
      this.props.onLayoutChange()
    }
  }

  // Event handler methods

  onDismissCapture(event) {
    const modalEl = this.modalRef.current
    if (!modalEl) {
      return
    }
    let traverseEl = event.target
    while (traverseEl) {
      // Allow clicks on the LWE modal or its children
      if (traverseEl === modalEl) {
        return
      }
      // Allow clicks on ArticleMixButton to toggle the modal
      if (traverseEl.classList.contains('ArticleMixButton')) {
        return
      }
      // Walk up the DOM tree to determine if the click is inside the modal
      traverseEl = traverseEl.parentElement
    }
    this.props.onDismiss()
  }

  onFilterReset(event) {
    event.preventDefault()
    this.props.onFilterTextChange('')
  }

  onFilterSubmit(event) {
    event.preventDefault()
    this.props.onFilterSubmit()
  }

  onFilterTextChange(event) {
    this.props.onFilterTextChange(event.target.value)
  }

  onFilterTextClick(event) {
    /**
     * Work-around default behavior getting swallowed by NukaCarousel
     * @see https://app.asana.com/0/226524042134185/1159662537839081/f
     */
    event.target.focus()
  }

  onFilterTextKeyDown(event) {
    if (event.keyCode === keyCodesByName.escape) {
      this.onFilterReset(event)
    }
  }

  // Child component callbacks

  onCollectionClick(collection) {
    this.props.onCollectionClick({ collection })
  }

  // Component render methods

  renderHeader() {
    return (
      tag('header', {
        className: bem.element('header')
      },
        tag('div', {
          className: bem.element('title')
        },
          'Add to a collection'
        ),
        tag(CircleButton, {
          className: bem.element('close-button'),
          onClick: this.props.onDismiss
        })
      )
    )
  }

  renderFilterForm() {
    const { filterText } = this.props
    return (
      tag('form', {
        className: bem.element('filter-form'),
        onReset: this.onFilterReset,
        onSubmit: this.onFilterSubmit
      },
        tag('div', {
          className: bem.element('filter-field')
        },
          tag('input', {
            className: bem.element('filter-input'),
            autoComplete: 'off',
            autoCorrect: 'off',
            autoCapitalize: 'off',
            autoFocus: true,
            disabled: !this.props.isCollectionsLoaded,
            maxLength: MAX_COLLECTION_NAME_LENGTH,
            placeholder: 'Search for or create a collection',
            spellCheck: 'false',
            tabIndex: 1,
            type: 'text',
            value: filterText,
            onChange: this.onFilterTextChange,
            onClick: this.onFilterTextClick,
            onKeyDown: this.onFilterTextKeyDown
          }),
          tag(CircleButton, {
            className: bem.element('filter-icon', {
              reset: !!filterText,
              search: !filterText
            }),
            type: 'reset',
            onClick: stopPropagation
          })
        ),
        tag(CapsuleButton, {
          className: bem.element('filter-submit', {
            disabled: this.props.isCreateButtonDisabled,
            hidden: !this.props.isCreateButtonVisible
          }),
          disabled: this.props.isCreateButtonDisabled,
          type: 'submit',
          onClick: stopPropagation
        },
          'Create'
        )
      )
    )
  }

  renderCollectionsList() {
    if (!this.props.isCollectionsLoaded) {
      return (
        tag('div', {
          className: bem.element('list', 'loading')
        })
      )
    }
    if (this.props.isUnfilteredCollectionsEmpty) {
      return this.renderCreatePrompt()
    }
    if (this.props.isFilteredCollectionsEmpty) {
      return this.renderEmptySearch()
    }
    return (
      tag('div', {
        className: bem.element('list')
      },
        this.props.collections.map(item => (
          tag(AddToCollectionItem, {
            key: item.slug,
            className: bem.element('list-item'),
            item,
            onClick: this.onCollectionClick
          })
        ))
      )
    )
  }

  renderInnerCollectionsError() {
    return (
      tag('div', {
        className: bem.element('error')
      },
        tag('p', {
          className: bem.element('error-heading')
        },
          'Oops.'
        ),
        tag('p', {
          className: bem.element('error-subheading')
        },
          'Something went wrong. Give it another shot.'
        ),
        tag(CapsuleButton, {
          className: bem.element('error-retry'),
          onClick: this.props.onRetry
        },
          'Retry'
        ),
        tag(CapsuleButton, {
          className: bem.element('error-contact'),
          href: 'mailto:support@mix.com',
          onClick: this.onSupportClick
        },
          'Contact Support'
        )
      )
    )
  }

  renderCreatePrompt() {
    return (
      tag('div', {
        className: bem.element('create-prompt')
      },
        tag('div', {
          className: bem.element('arrow')
        }),
        tag('div', {
          className: bem.element('create-text')
        },
          'Create your first collection to ',
          'organize cool things to save.'
        )
      )
    )
  }

  renderEmptySearch() {
    return (
      tag('div', {
        className: bem.element('empty-search')
      },
        'No search results... Create ',
        tag('a', {
          className: bem.element('empty-search-create'),
          onClick: this.onFilterSubmit
        },
          `“${this.props.filterText}”?`
        )
      )
    )
  }

  renderSnackbar() {
    const { snackbarIcon, snackbarText } = this.props
    return (
      tag('div', {
        className: bem.element('snackbar')
      },
        snackbarIcon && tag('div', {
          className: bem.element('snackbar-icon', {
            [this.props.snackbarIcon]: true
          })
        }),
        tag('div', {
          className: bem.element('snackbar-text')
        },
          snackbarText
        )
      )
    )
  }

  renderUnmixButton() {
    const { isUnmixDisabled } = this.props
    return (
      tag('button', {
        className: bem.element('unmix-button', {
          disabled: isUnmixDisabled
        }),
        disabled: isUnmixDisabled,
        onClick: this.props.onUnmix
      },
        'Unmix'
      )
    )
  }

  renderFooter() {
    if (this.props.isSnackbarVisible) {
      return this.renderSnackbar()
    }
    if (this.props.isUnmixButtonVisible) {
      return this.renderUnmixButton()
    }
    return null
  }

  renderUnmixPrompt() {
    const { isUnmixDisabled } = this.props
    return (
      tag('div', {
        className: bem.element('inner', 'unmix')
      },
        tag('p', {
          className: bem.element('unmix-prompt')
        },
          'Do you want to remove this post from your profile?'
        ),
        tag(CapsuleButton, {
          className: bem.element('unmix-prompt-confirm'),
          disabled: isUnmixDisabled,
          onClick: this.props.onUnmixPromptConfirm
        },
          'Remove post'
        ),
        tag(CapsuleButton, {
          className: bem.element('unmix-prompt-cancel'),
          disabled: isUnmixDisabled,
          onClick: this.props.onUnmixPromptCancel
        },
          'Keep post'
        )
      )
    )
  }

  renderInner() {
    if (this.props.isUnmixPromptVisible) {
      return this.renderUnmixPrompt()
    }
    if (this.props.isCollectionsFailed) {
      return this.renderInnerCollectionsError()
    }
    return (
      tag('div', {
        className: bem.element('inner')
      },
        this.renderHeader(),
        this.renderFilterForm(),
        this.renderCollectionsList(),
        this.renderFooter()
      )
    )
  }

  // Root render method

  render() {
    return (
      tag('div', {
        ref: this.modalRef,
        className: classNames(
          this.props.className,
          bem.block()
        ),
        // Swallow click events to prevent going through/behind the modal
        onClick: preventDefault,
        onMouseEnter: this.onMouseEnter,
        onMouseLeave: this.onMouseLeave
      },
        this.state.hover && tag(ModalDepthModifier),
        this.renderInner()
      )
    )
  }
}

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')

  AddToCollectionModal.propTypes = {
    // Flags
    isCreateButtonDisabled: PropTypes.bool,
    isCreateButtonVisible: PropTypes.bool,
    isCollectionsFailed: PropTypes.bool,
    isCollectionsLoaded: PropTypes.bool,
    isFilteredCollectionsEmpty: PropTypes.bool,
    isSnackbarVisible: PropTypes.bool,
    isUnfilteredCollectionsEmpty: PropTypes.bool,
    isUnmixDisabled: PropTypes.bool,
    isUnmixButtonVisible: PropTypes.bool,
    isUnmixPromptVisible: PropTypes.bool,
    // Values
    className: PropTypes.string,
    collections: PropTypes.arrayOf(
      PropTypes.shape(CollectionModelRecord.propTypes)
    ),
    filterText: PropTypes.string,
    snackbarIcon: PropTypes.string,
    snackbarText: PropTypes.string,
    // Callbacks
    onCollectionClick: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired,
    onFilterSubmit: PropTypes.func.isRequired,
    onFilterTextChange: PropTypes.func.isRequired,
    onLayoutChange: PropTypes.func,
    onRetry: PropTypes.func.isRequired,
    onUnmix: PropTypes.func.isRequired,
    onUnmixPromptCancel: PropTypes.func.isRequired,
    onUnmixPromptConfirm: PropTypes.func.isRequired
  }
}

module.exports = AddToCollectionModal
